import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowRight, Check, ChevronLeft, ChevronRight, Dumbbell, Pencil, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { BotonEnlace, Button } from '@/components/Button';
import { Card, Dato, EstadoVacio, Pastilla, Seccion } from '@/components/Card';
import { DateField, TextField } from '@/components/Field';
import { Fila, Filas } from '@/components/Fila';
import { leerSettings } from '@/db/schema';
import {
  agregarSerie,
  borrarEntrenamiento,
  cambiarFechaDeEntrenamiento,
  guardarNotasEntrenamiento,
  leerEntrenamiento,
  leerHistorial,
  leerHistorialDeEjercicio,
  restaurarSerie,
  type EjercicioEnCurso,
  type EntrenamientoDetalle,
} from '@/db/entrenamientos';
import { mensajeDeError } from '@/db/tx';
import type { UnidadPeso, Workout, WorkoutSet } from '@/db/types';
import {
  diaDelMes,
  esFechaLocal,
  etiquetaMes,
  finMes,
  finSemana,
  inicioMes,
  inicioSemana,
  nombreMes,
  rangoDeFechas,
  sumarMeses,
  type FechaLocal,
} from '@/domain/dates';
import { resumenEntrenamiento, textoDuracion, textoSerie, textoVolumen, type ResumenEntrenamiento } from '@/domain/entrenamiento';
import { fechaCorta } from '@/domain/hoy';
import { ETIQUETAS_MODALIDAD, modalidadDe, textoPausaYVariante } from '@/domain/modalidad';
import { formatearNumero } from '@/domain/numbers';
import { horaLocalDe } from '@/domain/time';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { AvisoDeshacer, FilaSerie } from './EntrenamientoPage';

const INICIALES = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const mayuscula = (t: string) => t.charAt(0).toLocaleUpperCase('es-MX') + t.slice(1);

/** El primer día del mes de `?mes=AAAA-MM`; si falta, no es válido o es futuro, el mes de hoy. */
function mesDe(parametro: string | null, hoy: FechaLocal): FechaLocal {
  const fecha = parametro !== null && /^\d{4}-\d{2}$/.test(parametro) ? `${parametro}-01` : null;
  return fecha !== null && esFechaLocal(fecha) && fecha <= hoy ? fecha : inicioMes(hoy);
}

function textoSeries(n: number): string {
  return n === 1 ? '1 serie' : `${n} series`;
}

/** "45 min · 12 series · 2,450 lb"; sin volumen si no hubo carga (en casa). */
function detalleDe(r: ResumenEntrenamiento, unidad: UnidadPeso): string {
  return [textoDuracion(r.duracionSeg), textoSeries(r.seriesHechas), r.volumenKg > 0 ? textoVolumen(r.volumenKg, unidad) : null]
    .filter((x) => x !== null)
    .join(' · ');
}

// ---------------------------------------------------------------------------
// Lista por mes con su calendario (Req. 13.1, 13.4)
// ---------------------------------------------------------------------------

export function HistorialPage() {
  const hoy = useFechaHoy();
  const [parametros, setParametros] = useSearchParams();
  const inicio = mesDe(parametros.get('mes'), hoy);
  const fin = finMes(inicio);
  const lista = useLiveQuery(() => leerHistorial(inicio, fin), [inicio, fin]);
  const ajustes = useLiveQuery(() => leerSettings(), []);
  if (lista === undefined || ajustes === undefined) return null;

  const esteMes = inicio === inicioMes(hoy);
  const dias = [...new Set(lista.map((x) => x.workout.fecha))].sort();
  const irA = (mes: FechaLocal) => setParametros(mes === inicioMes(hoy) ? {} : { mes: mes.slice(0, 7) }, { replace: true });

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Tu historial."
        descripcion="Tus entrenamientos terminados, del más reciente al más antiguo. Puedes corregirlos."
        volver={{ a: '/gym', texto: 'Gym', etiqueta: 'Volver a Gym' }}
      />

      <nav aria-label="Cambiar de mes" className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => irA(sumarMeses(inicio, -1))}
          className="inline-flex min-h-11 items-center gap-1 rounded-xl px-2 text-[13px] text-marca active:opacity-60"
        >
          <ChevronLeft className="size-4" />
          Mes anterior
        </button>
        <span className="text-[15px] font-medium text-texto">{mayuscula(etiquetaMes(inicio))}</span>
        <button
          type="button"
          disabled={esteMes}
          onClick={() => irA(sumarMeses(inicio, 1))}
          className="inline-flex min-h-11 items-center gap-1 rounded-xl px-2 text-[13px] text-marca active:opacity-60 disabled:text-texto-suave"
        >
          Mes siguiente
          <ChevronRight className="size-4" />
        </button>
      </nav>

      <Calendario inicio={inicio} dias={new Set(dias)} hoy={hoy} />
      <p className="text-[13px] text-texto-suave">
        {dias.length === 0
          ? `Sin entrenamientos en ${nombreMes(inicio)}.`
          : `${dias.length === 1 ? '1 día' : `${dias.length} días`} con entrenamiento en ${nombreMes(inicio)}.`}
        <span className="sr-only">
          {dias.length === 0 ? '' : ` Los días ${dias.map((d) => diaDelMes(d)).join(', ')}.`}
        </span>
      </p>

      {lista.length === 0 ? (
        <EstadoVacio
          icono={Dumbbell}
          titulo="Nada que mostrar este mes."
          descripcion="Cuando termines un entrenamiento aparece aquí, con su duración, sus series y su volumen."
        />
      ) : (
        <Filas>
          {lista.map(({ workout, resumen }) => (
            <Fila
              key={workout.id}
              titulo={workout.nombre}
              detalle={`${fechaCorta(workout.fecha)} · ${detalleDe(resumen, ajustes.unidadPeso)}`}
              a={`/gym/historial/${workout.id}`}
              {...(modalidadDe(workout) === 'casa' ? { extra: <Pastilla>Casa</Pastilla> } : {})}
            />
          ))}
        </Filas>
      )}
    </div>
  );
}

/**
 * El mes con los días entrenados marcados (Req. 13.4). Es solo un resumen
 * visual: la lista de abajo tiene los enlaces y el texto de arriba lo dice
 * para lectores de pantalla.
 */
function Calendario({ inicio, dias, hoy }: { inicio: FechaLocal; dias: ReadonlySet<FechaLocal>; hoy: FechaLocal }) {
  const fechas = rangoDeFechas(inicioSemana(inicio, 1), finSemana(finMes(inicio), 1));
  const mes = inicio.slice(0, 7);
  return (
    <div aria-hidden="true" className="rounded-panel bg-superficie p-4">
      <div className="grid grid-cols-7 text-center text-[11px] text-texto-suave">
        {INICIALES.map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-7 gap-y-1.5 text-center">
        {fechas.map((f) => {
          if (f.slice(0, 7) !== mes) return <span key={f} />;
          const entreno = dias.has(f);
          const clases = entreno
            ? 'bg-marca font-medium text-sobre-marca'
            : f === hoy
              ? 'border border-marca text-texto'
              : 'text-texto-suave';
          return (
            <span key={f} className="grid place-items-center">
              <span className={`grid size-8 place-items-center rounded-full text-[13px] tabular-nums ${clases}`}>{diaDelMes(f)}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Un entrenamiento terminado: detalle, correcciones y borrar (Req. 13.2, 13.3)
// ---------------------------------------------------------------------------

export function EntrenamientoHistorialPage() {
  const { id = '' } = useParams();
  const hoy = useFechaHoy();
  const detalle = useLiveQuery(() => leerEntrenamiento(id), [id]);
  const ajustes = useLiveQuery(() => leerSettings(), []);
  if (detalle === undefined || ajustes === undefined) return null;
  if (detalle === null || detalle.workout.estado !== 'terminado') {
    return (
      <div>
        <PageHeader titulo="No está." volver={{ a: '/gym/historial', texto: 'Historial' }} />
        {detalle?.workout.estado === 'en_curso' ? (
          <EstadoVacio titulo="Ese entrenamiento sigue en curso.">
            <BotonEnlace to="/gym/entrenamiento" icono={ArrowRight}>
              Continuar entrenamiento
            </BotonEnlace>
          </EstadoVacio>
        ) : (
          <EstadoVacio titulo="Ese entrenamiento ya no está en tu historial." />
        )}
      </div>
    );
  }
  return <DetalleDeHistorial key={detalle.workout.id} detalle={detalle} unidadPeso={ajustes.unidadPeso} hoy={hoy} />;
}

function DetalleDeHistorial({ detalle, unidadPeso, hoy }: { detalle: EntrenamientoDetalle; unidadPeso: UnidadPeso; hoy: FechaLocal }) {
  const navegar = useNavigate();
  const { workout, ejercicios } = detalle;
  const [editando, setEditando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [fecha, setFecha] = useState<string>(workout.fecha);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const r = resumenEntrenamiento(workout, ejercicios.flatMap((e) => e.series));
  const casa = modalidadDe(workout) === 'casa';
  const volver = workout.fecha.slice(0, 7) === hoy.slice(0, 7) ? '/gym/historial' : `/gym/historial?mes=${workout.fecha.slice(0, 7)}`;

  async function accion<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(mensajeDeError(e));
      return undefined;
    }
  }

  async function cambiarFecha() {
    setAviso(null);
    const ok = await accion(async () => {
      await cambiarFechaDeEntrenamiento(workout.id, fecha, hoy, new Date());
      return true;
    });
    if (ok === true && esFechaLocal(fecha)) setAviso(`Fecha cambiada: ahora es del ${fechaCorta(fecha)}, a la misma hora.`);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        antetitulo={`${fechaCorta(workout.fecha)} · ${ETIQUETAS_MODALIDAD[modalidadDe(workout)]}`}
        titulo={workout.nombre}
        descripcion={`Empezó a las ${horaLocalDe(workout.inicio)}.`}
        volver={{ a: volver, texto: 'Historial', etiqueta: 'Volver al historial' }}
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo">
          {error}
        </Banner>
      )}
      {aviso !== null && <Banner tono="exito" titulo={aviso} />}

      <Card>
        <dl className="divide-y divide-borde">
          <Dato etiqueta="Duración" valor={textoDuracion(r.duracionSeg)} />
          <Dato
            etiqueta="Series hechas"
            valor={String(r.seriesHechas)}
            {...(r.calentamientos > 0 ? { detalle: `Más ${r.calentamientos} de calentamiento, que no cuentan.` } : {})}
          />
          {casa && <Dato etiqueta="Repeticiones" valor={formatearNumero(r.repeticiones, { decimales: 0 })} />}
          {(!casa || r.volumenKg > 0) && (
            <Dato
              etiqueta="Volumen"
              valor={textoVolumen(r.volumenKg, unidadPeso)}
              {...(r.seriesEnPlacas > 0 ? { detalle: `${r.seriesEnPlacas} series en placas no entran: cada máquina es distinta.` } : {})}
            />
          )}
        </dl>
      </Card>

      {editando ? (
        <>
          <Seccion titulo="Fecha" descripcion="El inicio y el fin se mueven a ese día, a la misma hora.">
            <DateField etiqueta="Fecha del entrenamiento" valor={fecha} onCambio={setFecha} ayuda="Hoy o antes." />
            <Button variante="secundario" disabled={fecha === workout.fecha} onClick={() => void cambiarFecha()}>
              Cambiar la fecha
            </Button>
          </Seccion>

          {ejercicios.length === 0 && <p className="text-[13px] text-texto-suave">Este entrenamiento no tiene series.</p>}
          {ejercicios.map((e) => (
            // Con la fecha en la clave: al cambiarla, "Deshacer" de una serie borrada ya no la
            // devuelve con la hora del día anterior (0.3.9)
            <EjercicioEditable key={`${e.we.id}-${workout.fecha}`} e={e} unidadPeso={unidadPeso} onAccion={accion} />
          ))}

          <NotasEditables workout={workout} onAccion={accion} />

          <Button icono={Check} onClick={() => setEditando(false)}>
            Listo
          </Button>
        </>
      ) : (
        <>
          <Seccion titulo="Lo que hiciste">
            {ejercicios.length === 0 ? (
              <p className="text-[13px] text-texto-suave">Este entrenamiento no tiene series.</p>
            ) : (
              <ul className="divide-y divide-borde">
                {ejercicios.map((e) => {
                  const detalleCasa = textoPausaYVariante(e.we);
                  return (
                    <li key={e.we.id} className="py-3">
                      <p className="text-[14px] font-medium text-texto">{e.ejercicio?.nombre ?? 'Ejercicio'}</p>
                      <p className="text-[12px] text-texto-suave">{e.series.map((s) => textoSerie(s)).join(', ')}</p>
                      {detalleCasa !== '' && <p className="text-[12px] text-texto-suave">{detalleCasa}</p>}
                    </li>
                  );
                })}
              </ul>
            )}
          </Seccion>
          {workout.notas !== null && <p className="text-[13px] leading-relaxed text-texto-suave">{workout.notas}</p>}

          <div className="grid gap-2">
            <Button variante="secundario" icono={Pencil} onClick={() => setEditando(true)}>
              Corregir
            </Button>
            {borrando ? (
              <div className="space-y-2 rounded-panel bg-alerta-suave p-4">
                <p className="text-[13px] text-texto">
                  ¿Borrar este entrenamiento? Se quitan sus ejercicios y sus series, y deja de contar en la vez pasada.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variante="peligro"
                    onClick={() =>
                      void accion(async () => {
                        await borrarEntrenamiento(workout.id);
                        void navegar(volver, { replace: true });
                      })
                    }
                  >
                    Sí, borrar
                  </Button>
                  <Button variante="secundario" onClick={() => setBorrando(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <Button variante="fantasma" icono={Trash2} onClick={() => setBorrando(true)}>
                Borrar entrenamiento
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Las series de un ejercicio, para corregirlas: ya están hechas; la nueva también. */
function EjercicioEditable({
  e,
  unidadPeso,
  onAccion,
}: {
  e: EjercicioEnCurso;
  unidadPeso: UnidadPeso;
  onAccion: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
}) {
  const nombre = e.ejercicio?.nombre ?? 'Ejercicio';
  const [borrada, setBorrada] = useState<{ serie: WorkoutSet; numero: number } | null>(null);
  return (
    <article aria-labelledby={`hist-${e.we.id}`} className="rounded-panel bg-superficie p-4">
      <h2 id={`hist-${e.we.id}`} className="text-[17px] leading-snug tracking-[-0.25px] text-texto">
        {nombre}
      </h2>
      <p className="mt-0.5 text-[12px] text-texto-suave">
        Toca el número de una serie para su tipo, el RIR o borrarla. Al borrar la última, se quita el ejercicio.
      </p>
      <ol className="mt-3 space-y-2">
        {e.series.map((s, i) => {
          const unidad = s.pesoCapturado?.unidad ?? e.ejercicio?.unidadCaptura ?? unidadPeso;
          return (
            <FilaSerie
              key={`${s.id}-${unidad}`}
              serie={s}
              numero={i + 1}
              tipo={e.ejercicio?.tipo ?? 'peso_reps'}
              porLado={e.we.porLado === true}
              unidad={unidad}
              nombreEjercicio={nombre}
              onAccion={onAccion}
              {...(e.series.length === 1
                ? {
                    avisoAlQuitar: `Es la única serie de ${nombre}: al borrarla, el ejercicio sale de este entrenamiento, con su variante y sus notas, y no se puede volver a agregar aquí.`,
                  }
                : { onBorrada: (serie: WorkoutSet, numero: number) => setBorrada({ serie, numero }) })}
            />
          );
        })}
      </ol>
      {borrada !== null && (
        <AvisoDeshacer
          texto={`Borraste la serie ${borrada.numero} (${textoSerie(borrada.serie)}). Las demás no cambiaron.`}
          onDeshacer={() =>
            void onAccion(async () => {
              await restaurarSerie(borrada.serie);
              setBorrada(null);
            })
          }
          onCerrar={() => setBorrada(null)}
        />
      )}
      <div className="mt-3">
        <Button variante="secundario" icono={Plus} onClick={() => void onAccion(() => agregarSerie(e.we.id))}>
          Agregar serie
        </Button>
      </div>
    </article>
  );
}

function NotasEditables({ workout, onAccion }: { workout: Workout; onAccion: <T>(fn: () => Promise<T>) => Promise<T | undefined> }) {
  const [notas, setNotas] = useState(workout.notas ?? '');
  return (
    <div onBlur={() => void onAccion(() => guardarNotasEntrenamiento(workout.id, notas))}>
      <TextField etiqueta="Notas del entrenamiento" valor={notas} onCambio={setNotas} maxLength={500} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Historial de un ejercicio (Req. 13.5), en la pantalla del ejercicio
// ---------------------------------------------------------------------------

export function HistorialDeEjercicio({ exerciseId }: { exerciseId: string }) {
  const sesiones = useLiveQuery(() => leerHistorialDeEjercicio(exerciseId), [exerciseId]);
  if (sesiones === undefined) return null;
  return (
    <Seccion
      titulo="Tu historial con este ejercicio"
      extra={sesiones.length === 1 ? '1 sesión' : `${sesiones.length} sesiones`}
    >
      {sesiones.length === 0 ? (
        <p className="text-[13px] leading-relaxed text-texto-suave">Todavía no está en un entrenamiento terminado.</p>
      ) : (
        <ul className="divide-y divide-borde">
          {sesiones.map(({ workout, series }) => (
            <li key={workout.id}>
              <Link to={`/gym/historial/${workout.id}`} className="flex min-h-14 items-center gap-3 py-3 active:opacity-60">
                <span className="grid min-w-0 flex-1 gap-1">
                  <span className="flex flex-wrap items-center gap-1.5 text-[14px] font-medium text-texto">
                    {fechaCorta(workout.fecha)} · {workout.nombre}
                    {modalidadDe(workout) === 'casa' && <Pastilla>Casa</Pastilla>}
                  </span>
                  <span className="text-[12px] text-texto-suave">{series.map((s) => textoSerie(s)).join(', ')}</span>
                </span>
                <ChevronRight className="size-4 text-texto-suave" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Seccion>
  );
}
